/**
 * TextAnnotationDialog - Dialog for entering and styling text annotations.
 *
 * Provides a text input field and style customization controls (font, size,
 * color, background) for placing text annotations on the map.
 *
 * Validates: Requirements 40.2, 40.4
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  closeTextDialog,
  setTextStyle,
  addDrawing,
  updateDrawing,
} from '../store/slices/drawingSlice';
import type { TextStyle } from '../types/drawing';
import { drawingToolService } from '../services/DrawingToolService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FONT_OPTIONS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Verdana',
  'Trebuchet MS',
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TextAnnotationDialogProps {
  /** Optional override; default applies to map via DrawingToolService */
  onConfirm?: (text: string, style: TextStyle) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TextAnnotationDialog: React.FC<TextAnnotationDialogProps> = ({ onConfirm }) => {
  const dispatch = useAppDispatch();
  const { textDialogOpen, textStyle, editingDrawingId, drawings, textPlacementCoords } =
    useAppSelector((state) => state.drawing);

  const [text, setText] = useState('');
  const [localStyle, setLocalStyle] = useState<TextStyle>({ ...textStyle });

  // Load existing text when editing
  useEffect(() => {
    if (editingDrawingId) {
      const drawing = drawings.find((d) => d.id === editingDrawingId);
      if (drawing) {
        setText(drawing.text || '');
        setLocalStyle(drawing.textStyle || { ...textStyle });
      }
    } else {
      setText('');
      setLocalStyle({ ...textStyle });
    }
  }, [textDialogOpen, editingDrawingId, drawings, textStyle]);

  const handleConfirm = () => {
    if (!text.trim()) return;
    dispatch(setTextStyle(localStyle));
    if (onConfirm) {
      onConfirm(text.trim(), localStyle);
    } else if (editingDrawingId) {
      try {
        drawingToolService.updateTextAnnotation(editingDrawingId, text.trim(), localStyle);
      } catch {
        /* service may not be init in tests */
      }
      dispatch(
        updateDrawing({
          id: editingDrawingId,
          updates: { text: text.trim(), textStyle: localStyle },
        })
      );
    } else if (textPlacementCoords) {
      try {
        const drawing = drawingToolService.addTextAnnotation(
          textPlacementCoords,
          text.trim(),
          localStyle
        );
        dispatch(addDrawing(drawing));
      } catch {
        /* ignore */
      }
    }
    dispatch(closeTextDialog());
  };

  const handleCancel = () => {
    dispatch(closeTextDialog());
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog
      open={textDialogOpen}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="text-annotation-dialog-title"
    >
      <DialogTitle id="text-annotation-dialog-title">
        {editingDrawingId ? 'Edit Text Annotation' : 'Add Text Annotation'}
      </DialogTitle>
      <DialogContent>
        {/* Text Input */}
        <TextField
          autoFocus
          margin="dense"
          label="Annotation Text"
          fullWidth
          multiline
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter annotation text..."
          aria-label="Annotation text"
        />

        <Divider sx={{ my: 2 }} />

        {/* Text Style Controls */}
        <Typography variant="subtitle2" gutterBottom>
          Text Style
        </Typography>

        {/* Font Family */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="font-select-label">Font</InputLabel>
          <Select
            labelId="font-select-label"
            value={localStyle.font}
            label="Font"
            onChange={(e) => setLocalStyle({ ...localStyle, font: e.target.value })}
            aria-label="Font family"
          >
            {FONT_OPTIONS.map((font) => (
              <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Font Size */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption">Font Size: {localStyle.fontSize}px</Typography>
          <Slider
            value={localStyle.fontSize}
            onChange={(_e, value) => setLocalStyle({ ...localStyle, fontSize: value as number })}
            min={8}
            max={48}
            step={1}
            size="small"
            aria-label="Font size"
          />
        </Box>

        {/* Font Color */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" sx={{ width: 100 }}>
            Text Color
          </Typography>
          <input
            type="color"
            value={localStyle.fontColor}
            onChange={(e) => setLocalStyle({ ...localStyle, fontColor: e.target.value })}
            style={{ width: 40, height: 28, border: 'none', cursor: 'pointer' }}
            aria-label="Text color"
          />
        </Box>

        {/* Background Color */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" sx={{ width: 100 }}>
            Background
          </Typography>
          <input
            type="color"
            value={localStyle.backgroundColor || '#ffffff'}
            onChange={(e) => setLocalStyle({ ...localStyle, backgroundColor: e.target.value })}
            style={{ width: 40, height: 28, border: 'none', cursor: 'pointer' }}
            aria-label="Background color"
          />
        </Box>

        {/* Preview */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            textAlign: 'center',
            backgroundColor: localStyle.backgroundColor || 'transparent',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Preview
          </Typography>
          <span
            style={{
              fontFamily: localStyle.font,
              fontSize: `${localStyle.fontSize}px`,
              color: localStyle.fontColor,
            }}
          >
            {text || 'Sample Text'}
          </span>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!text.trim()}>
          {editingDrawingId ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TextAnnotationDialog;
